import { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserContext } from '../../UserContext';
import axios from '../../axios';
import { EyeIcon, EyeSlashIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid';
import notify from '../../Utils/notify';

const Login = () => {
    const { user, setUser, setAuth, setSucursal } = useContext(UserContext);
    const navigate = useNavigate();
    const [data, setData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await axios.get('/api/user');
                console.log("User fetched:", res.data);
                setAuth(true);
                setUser(res.data);
            } catch (e) {
                console.error("Error fetching user", e);
            }
        };

        if (user.id) {
            navigate('/admin/usuarios');
        } else {
            fetchUser();
        }
        // setAuth y setUser son funciones setState estables, no necesitan estar en dependencias
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.id, navigate]);

    const handleChange = useCallback((e) => {
        const { id, value } = e.target;
        setData(prevData => ({ ...prevData, [id]: value }));
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        const loadingToast = notify.loading('Iniciando sesión...');
       
        try {
            const res = await axios.post('/api/login', data);
            const { user, token_type, access_token } = res.data;
          
            setUser(user);
            setAuth(true);
            localStorage.setItem('access_token', `${token_type} ${access_token}`);
            axios.defaults.headers.common['Authorization'] = `${token_type} ${access_token}`;

            if (user.sucursals && user.sucursals.length > 0) {
                //revisamos si hay sucursales en localStorage y si esta sucursal pertenece al usuario, si no, seleccionamos la primera sucursal del usuario
                const storedSucursalId = localStorage.getItem('sucursal_selected');
                const userSucursalIds = user.sucursals.map(s => s.id.toString());
               
                
                if (!storedSucursalId || !userSucursalIds.includes(storedSucursalId)) {
                    const { id, nombre_sucursal } = user.sucursals[0];
                    setSucursal(nombre_sucursal);
                    localStorage.setItem('sucursal_selected', id);
                    localStorage.setItem('sucursal_name', nombre_sucursal);
                }
            }

            notify.dismiss(loadingToast);
            notify.success(`¡Bienvenido ${user.name || 'Usuario'}!`);
            navigate("/admin");
        } catch (e) {
            console.log("Login error response:", e.response);
            console.error("Login error", e);
            notify.dismiss(loadingToast);
            
            const message = e.response?.status === 401
                ? "Usuario o contraseña incorrectos. Por favor, verifica tus datos."
                : e.response?.data?.message || "Error al iniciar sesión. Intenta nuevamente.";
            
            notify.error(message);
        }
    }, [data, navigate, setAuth, setSucursal, setUser]);

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-center bg-cover bg-no-repeat m-0 p-0 bg-[#6DB3F2]"
        style={{ backgroundImage: "url('/images/bg-principal.png')" }}>
            {/* Contenedor transparente exterior */}
            <div className="rounded-lg flex items-center justify-center m-0 p-0 h-[76vh] w-[76%] max-[659px]:w-[95%] max-[659px]:h-auto max-[659px]:py-8 bg-white/10 shadow-[0_0_20px_-10px_rgba(255,255,255,1)]">
                {/* Contenedor transparente interior (más pequeño) */}
                <div className="rounded-lg flex flex-col items-center m-0 p-0 h-[75%] w-[75%] max-[659px]:w-[95%] max-[659px]:h-auto bg-white/10 shadow-[0_0_20px_-10px_rgba(255,255,255,1)] pt-8">
                    {/* Logo */}
                    <div className="flex items-center justify-center">
                        <img 
                            className="h-[154px] max-[659px]:h-[100px] rounded-[40%] shadow-[0_0_20px_-10px_rgba(255,255,255,1)]" 
                            src="/images/logo2.png" 
                            alt="Dento Metric System" 
                        />
                    </div>
                    
                    {/* Formulario */}
                    <div className="rounded-lg shadow-[0_0_15px_0_rgba(0,51,255,1)] bg-white h-[48vh] w-[40vw] max-[659px]:w-[90%] max-[659px]:h-auto p-4 max-[659px]:p-6 mt-8">
                        <div className="h-full flex items-center justify-center">
                            <div className="w-full px-2">
                                <form className="w-full" id="login-form" onSubmit={handleSubmit}>
                                    {/* Título con degradado */}
                                    <h5 className="font-semibold text-xl max-[659px]:text-lg flex items-center justify-center mb-6">
                                        <img src="/images/sesion.png" alt="" className="h-5 max-[659px]:h-4 pr-3" />
                                        <span className="bg-gradient-to-r from-[#0033ff] to-[#11cdef] bg-clip-text text-transparent">
                                            Iniciar Sesión
                                        </span>
                                    </h5>

                                    {/* Email */}
                                    <div className="mb-4">
                                        <label htmlFor="email" className="block text-base font-medium text-gray-700 mb-1">
                                            Email
                                        </label>
                                        <input 
                                            id="email" 
                                            type="email" 
                                            className="dento-input w-full px-3 py-2 border-b border-blue-600 focus:border-b-2 focus:outline-none bg-transparent box-border"
                                            onChange={handleChange} 
                                            required
                                        />
                                    </div>

                                    {/* Password */}
                                    <div className="mb-6">
                                        <label htmlFor="password" className="block text-base font-medium text-gray-700 mb-1">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <input 
                                                id="password" 
                                                type={showPassword ? 'text' : 'password'} 
                                                className="dento-input w-full px-3 py-2 border-b border-blue-600 focus:border-b-2 focus:outline-none bg-transparent pr-10 box-border"
                                                onChange={handleChange}
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-800"
                                            >
                                                {showPassword ? (
                                                    <EyeSlashIcon className="h-5 w-5" />
                                                ) : (
                                                    <EyeIcon className="h-5 w-5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Submit button */}
                                    <div className="text-right">
                                        <button 
                                            className="bg-[#0033ff] text-white px-6 py-2 rounded-md uppercase font-bold text-sm border-2 border-[#0033ff] hover:bg-white hover:text-[#0033ff] hover:shadow-[0_0_15px_0_rgba(0,51,255,1)] transition-all duration-300 inline-flex items-center gap-2 cursor-pointer"
                                            type="submit"
                                        >
                                            Ingresar
                                            <PaperAirplaneIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </form>

                                {/* Divider */}
                                <div className="flex items-center gap-2 my-5 w-full">
                                    <hr className="flex-1 border-gray-300" />
                                    <span className="text-gray-500">o</span>
                                    <hr className="flex-1 border-gray-300" />
                                </div>

                                {/* Forgot password */}
                                <div className="mt-5 text-center">
                                    <Link className="text-blue-600 hover:text-blue-800 hover:underline" to="/recuperar-contrasena">
                                        ¿Olvidaste tu contraseña?
                                    </Link>
                                </div>
                            </div> 
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;